import { store } from "../redux/store";
import { SET_SUBSCRIPTION_ACCESS } from "../redux/features/user/userSlice";
import { getSubscriptionAccess } from "../services/subscriptionService";

export const PAYMENTS_PATH = "/client/settings/payments";
export const MEMBER_PROFILE_PATH = "/client/settings/user_profile";

const PUBLIC_EXEMPT_PATH_PREFIXES = ["/client/login", "/client/signup"];

/** Personal account settings — available to all roles when subscription is locked. */
const PERSONAL_ACCOUNT_EXEMPT_PATH_PREFIXES = [
  MEMBER_PROFILE_PATH,
  "/client/settings/security",
];

/** Org billing / admin-only settings when subscription is locked. */
const ADMIN_ONLY_LOCKED_EXEMPT_PATH_PREFIXES = [
  PAYMENTS_PATH,
  "/client/settings/client_profile",
];

export const isBillingAdmin = (user) => (user?.role_id ?? 99) < 3;

export const isSubscriptionExemptPath = (pathname = "", user = null) => {
  if (
    PUBLIC_EXEMPT_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  ) {
    return true;
  }

  if (
    PERSONAL_ACCOUNT_EXEMPT_PATH_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    )
  ) {
    return true;
  }

  if (
    user &&
    isBillingAdmin(user) &&
    ADMIN_ONLY_LOCKED_EXEMPT_PATH_PREFIXES.some((prefix) =>
      pathname.startsWith(prefix),
    )
  ) {
    return true;
  }

  return false;
};

export const getSubscriptionLockedFallbackPath = (user) =>
  isBillingAdmin(user) ? PAYMENTS_PATH : MEMBER_PROFILE_PATH;

/** True when subscription state is known and access is denied. */
export const isSubscriptionLocked = (user) =>
  Boolean(user?.client_id) &&
  user?.subscription_status != null &&
  user?.can_access === false;

/** True before the first subscription access check completes this session. */
export const isSubscriptionAccessPending = (user) =>
  Boolean(user?.client_id) && user?.subscription_status == null;

export const applySubscriptionAccess = (access) => {
  if (!access) {
    return;
  }

  store.dispatch(SET_SUBSCRIPTION_ACCESS(access));
};

export const fetchAndApplySubscriptionAccess = async (clientId) => {
  const result = await getSubscriptionAccess(clientId);

  if (result?.success && result?.data?.access) {
    applySubscriptionAccess(result.data.access);
    return result.data.access;
  }

  return null;
};

export const clientHasAccess = (client) => {
  if (client?.can_access !== undefined) {
    return Boolean(client.can_access);
  }

  if (client?.subscription_access?.can_access !== undefined) {
    return Boolean(client.subscription_access.can_access);
  }

  if (!client?.subscription_end_date) {
    return true;
  }

  return new Date(client.subscription_end_date) >= new Date();
};

export const clientIsExpired = (client) => {
  if (client?.is_subscription_expired !== undefined) {
    return Boolean(client.is_subscription_expired);
  }

  if (client?.subscription_access?.is_subscription_expired !== undefined) {
    return Boolean(client.subscription_access.is_subscription_expired);
  }

  if (!client?.subscription_end_date) {
    return false;
  }

  return new Date(client.subscription_end_date) < new Date();
};

const LOCKED_NAV_LABELS = new Set([
  "Dashboard",
  "Programs",
  "Campaigns",
  "Reports and Analytics",
  "Insights",
]);

/**
 * Disable premium sidebar items when subscription/trial is expired.
 * Settings stays open; sub-links are filtered separately.
 */
export const applySubscriptionToSidebarOptions = (options, user) => {
  if (!isSubscriptionLocked(user)) {
    return options;
  }

  const settingsSubPaths = isBillingAdmin(user)
    ? ["/payments", "client_profile", "user_profile", "security"]
    : ["user_profile", "security"];

  return options.map((option) => {
    if (option.label === "Settings") {
      return {
        ...option,
        enable: true,
        path: isBillingAdmin(user)
          ? "/client/settings/client_profile"
          : MEMBER_PROFILE_PATH,
        option_list: (option.option_list ?? []).filter((sub) =>
          settingsSubPaths.some((fragment) => sub.path?.includes(fragment)),
        ),
      };
    }

    if (LOCKED_NAV_LABELS.has(option.label)) {
      return {
        ...option,
        enable: false,
        disabledTitle: "Subscribe to unlock this feature",
      };
    }

    return option;
  });
};

/**
 * Disable settings sub-nav items (e.g. Integrations) when locked.
 */
export const applySubscriptionToSubSidebarOptions = (options, user) => {
  if (!isSubscriptionLocked(user)) {
    return options.map((option) => ({ ...option, disabled: false }));
  }

  return options.map((option) => {
    const disabled = !isSubscriptionExemptPath(option.path ?? "", user);

    return {
      ...option,
      disabled,
      disabledTitle: disabled ? "Subscribe to unlock this feature" : undefined,
    };
  });
};
